import { Component, OnDestroy, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { NotificationService } from '../notification/notification.service';

interface FarmyKnowledge {
  id: number;
  text: string;
  createdAt: string;
  updatedAt: string | null;
  createdBy: string | null;
}

interface FarmyJob {
  id: number;
  status: 'pending' | 'running' | 'done' | 'error';
  requestedBy: string | null;
  createdAt: string;
  finishedAt: string | null;
  summary: string | null;
  error: string | null;
}

interface FarmyOverview {
  botUserId: number | null;
  enabled: boolean;
  workerOnline: boolean;
  workerSeenAt: string | null;
  workerInfo: string | null;
  knowledge: FarmyKnowledge[];
  proposals: { id: number; text: string; createdAt: string }[];
  jobs: FarmyJob[];
}

/** Farmy 🏭 oldala: a tudástára (amit mindig figyelembe vesz), a javaslatai, a műhelyi mini PC állapota. */
@Component({
  selector: 'app-farmy',
  templateUrl: './farmy.component.html',
  styleUrl: './farmy.component.scss',
})
export class FarmyComponent implements OnInit, OnDestroy {
  loading = true;
  loadFailed = false;
  saving = false;
  data: FarmyOverview | null = null;
  draft = '';
  editing: Record<number, string> = {};
  expanded: Record<number, boolean> = {};

  readonly examples = [
    'Gyártási napok: hétfőtől péntekig, a leszedést 8 és 16 óra között tudjuk vállalni.',
    'A Jump and Hide termékekből legalább 2 hétre elegendő készlet legyen.',
    'Éjszakára csak hosszú (6 óránál hosszabb) lemezt indítsunk, hogy reggelig ne álljon a gép.',
    'Az SOS prioritás csak sürgős vevői rendelésnél kell.',
  ];

  readonly statusLabel: Record<FarmyJob['status'], string> = { pending: 'Vár', running: 'Dolgozik', done: 'Kész', error: 'Hiba' };

  private timer: ReturnType<typeof setInterval> | null = null;

  constructor(private http: HttpClient, private notification: NotificationService) {}

  ngOnInit(): void {
    this.load();
    this.timer = setInterval(() => this.load(true), 30_000);
  }

  ngOnDestroy(): void {
    if (this.timer) clearInterval(this.timer);
  }

  load(silent = false): void {
    if (!silent) this.loading = true;
    this.http.get<FarmyOverview>('farmy/overview').subscribe({
      next: (data) => this.apply(data),
      error: () => {
        this.loading = false;
        this.loadFailed = !this.data;
      },
    });
  }

  private apply(data: FarmyOverview): void {
    this.data = data;
    this.loading = false;
    this.loadFailed = false;
    this.saving = false;
  }

  private fail(e: any, fallback: string): void {
    this.saving = false;
    this.notification.show(e?.error?.message || fallback, 'danger');
  }

  get chatLink(): string | null {
    return this.data?.botUserId ? `/chat/u-${this.data.botUserId}` : null;
  }

  get workerLabel(): string {
    if (!this.data) return '';
    if (!this.data.enabled) return 'Kikapcsolva';
    return this.data.workerOnline ? 'A mini PC fut' : 'A mini PC nem elérhető';
  }

  toggleEnabled(): void {
    if (!this.data) return;
    this.http.patch<FarmyOverview>('farmy/settings', { enabled: !this.data.enabled }).subscribe({
      next: (data) => this.apply(data),
      error: (e) => this.fail(e, 'Nem sikerült módosítani.'),
    });
  }

  add(): void {
    const text = this.draft.trim();
    if (!text || this.saving) return;
    this.saving = true;
    this.http.post<FarmyOverview>('farmy/knowledge', { text }).subscribe({
      next: (data) => {
        this.draft = '';
        this.apply(data);
        this.notification.show('Farmy megjegyezte. ✅', 'success');
      },
      error: (e) => this.fail(e, 'Nem sikerült menteni.'),
    });
  }

  useExample(text: string): void {
    this.draft = this.draft.trim() ? `${this.draft.trim()}\n${text}` : text;
  }

  startEdit(k: { id: number; text: string }): void {
    this.editing[k.id] = k.text;
  }

  cancelEdit(id: number): void {
    delete this.editing[id];
  }

  save(id: number, approve = false): void {
    const text = (this.editing[id] ?? '').trim();
    const body: { text?: string; approve?: boolean } = approve ? { approve: true } : {};
    if (this.editing[id] !== undefined) {
      if (!text) return;
      body.text = text;
    }
    this.saving = true;
    this.http.patch<FarmyOverview>(`farmy/knowledge/${id}`, body).subscribe({
      next: (data) => {
        delete this.editing[id];
        this.apply(data);
      },
      error: (e) => this.fail(e, 'Nem sikerült menteni.'),
    });
  }

  remove(id: number, question = 'Törlöd ezt a bejegyzést Farmy tudástárából?'): void {
    if (!confirm(question)) return;
    this.http.delete<FarmyOverview>(`farmy/knowledge/${id}`).subscribe({
      next: (data) => this.apply(data),
      error: (e) => this.fail(e, 'Nem sikerült törölni.'),
    });
  }
}
